import { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Layout } from '@/components/Layout/Layout';
import { Main } from '@/components/Main/Main';
import { RichText } from '@/components/RichText/RichText';
import { Section } from '@/components/Section/Section';
// Components
import { Context } from '@/components/Store';
import { ActionTypes } from '@/components/Store.d';
import { languageStrings } from '@/l18n/de';
// Modules
import { config } from '@/lib/config';
import { errorMessages, errorPageMessages } from '@/lib/errorMessages';
import { log } from '@/lib/log';

type ErrorMessage = {
	title: string;
	body: string;
	link?: string;
	linkTitle?: string;
	showTryAgain?: boolean;
};

const SESSION_EXPIRED_CODES = new Set(['440', 'authorizationDenied', 'sepaSessionExpired']);

const ErrorPage = () => {
	const [state, dispatch] = useContext(Context);
	const [errorMessage, setErrorMessage] = useState<ErrorMessage>({ title: '', body: '' });
	const location = useLocation();

	const code = new URLSearchParams(location.search).get('code') ?? '';

	useEffect(() => {
		void (async () => {
			const { setTitle } = await import('@/lib/util');
			setTitle('Fehlerseite');
		})();

		const sessionState = JSON.parse(sessionStorage.getItem('sessionData') ?? '');
		if (sessionState) {
			dispatch({ type: ActionTypes.restoreFromStorage, payload: sessionState });
		}

		dispatch({
			type: ActionTypes.setCurrentPage,
			payload: { key: 'currentPage', value: 'errorPage' }
		});
		dispatch({ type: ActionTypes.showProductOnRight, payload: false });

		void handleErrorMessages();
		// eslint-disable-next-line
	}, []);

	const handleErrorMessages = async (): Promise<void> => {
		const urlParams = Object.fromEntries(new URLSearchParams(location.search).entries());
		const errorCode = Array.isArray(urlParams?.code) ? urlParams.code[0] : urlParams?.code;

		log(
			'Handling error message',
			errorCode && errorMessages[errorCode as keyof typeof errorMessages]
		);

		switch (true) {
			case urlParams.code === '404':
				setErrorMessage(errorPageMessages.pageNotFound);
				break;

			case SESSION_EXPIRED_CODES.has(urlParams.code ?? ''):
				setErrorMessage({ ...errorPageMessages.sessionExpiredUnknownProduct });
				break;

			case urlParams.code === 'activeProduct':
				setErrorMessage(errorPageMessages.activeProduct);
				break;

			case urlParams.code === 'paypalCancelled':
				setErrorMessage(errorPageMessages.paypalCancelled);
				break;

			case urlParams.code === 'existingService':
			case urlParams.code === 'existingSubscription':
				setErrorMessage(errorPageMessages.existingSubscription);
				break;

			case urlParams.code === 'emailInUse':
				setErrorMessage(errorPageMessages.emailInUse);
				break;

			case urlParams.code === 'accountNotFound':
				setErrorMessage(errorPageMessages.accountNotFound);
				break;

			case Boolean(errorCode && errorMessages[errorCode as keyof typeof errorMessages]):
				if (errorCode) {
					const errorMsg = errorMessages[errorCode as keyof typeof errorMessages];
					const title =
						'title' in errorMsg && errorMsg.title
							? errorMsg.title
							: errorPageMessages.default.title;
					const body =
						'message' in errorMsg && errorMsg.message
							? errorMsg.message
							: errorPageMessages.default.body;
					const link = 'link' in errorMsg ? (errorMsg.link as string) : undefined;
					const showTryAgain = 'showTryAgain' in errorMsg ? errorMsg.showTryAgain : undefined;
					setErrorMessage({ body, link, showTryAgain, title });
				}
				break;

			default:
				setErrorMessage(errorPageMessages.default);
		}
	};

	const showTryAgain =
		errorMessage.showTryAgain !== false && Boolean(state?.entryPage || state?.bundle);

	// For session-expired errors where we know the entry page, render a dynamic
	// link directly — the URL comes from state so it can't live in the static
	// errorPageMessages strings.
	const sessionExpiredWithBundle =
		SESSION_EXPIRED_CODES.has(code) && state?.bundle && state.entryPage;

	return (
		<Layout className="layout-split">
			<Main>
				<Section className="section-error">
					<h1>{errorMessage.title}</h1>
					<div>
						{sessionExpiredWithBundle ? (
							<RichText
								components={{
									'bundle-link': <a href={state.entryPage} title={state.bundle!.name} />,
									'offers-link': <a href="https://abo.derstandard.at" title="DerStandard.at ABO" />
								}}
								message={`<p>Sie können die Bestellung erneut starten: <bundle-link>${state.bundle!.name}</bundle-link></p><p>Alle aktuellen Angebote finden Sie unter <offers-link>abo.derstandard.at</offers-link></p>`}
							/>
						) : (
							<RichText
								components={{
									link: errorMessage.link ? (
										<a href={errorMessage.link} title={errorMessage.linkTitle} />
									) : undefined
								}}
								message={errorMessage.body}
							/>
						)}
					</div>
					{showTryAgain ? (
						<p>
							<Link to={state?.entryPage || config.routes.product}>
								{languageStrings.buttons.tryAgain}
							</Link>
						</p>
					) : null}
				</Section>
			</Main>
		</Layout>
	);
};

export default ErrorPage;
